import { Component, OnInit, ViewChild } from "@angular/core";
import { AlertController, IonContent, ModalController } from "@ionic/angular";
import { TranslocoService } from "@ngneat/transloco";
import { LoadingService } from "../../../configuration/services/loading.service";
import { ChecklistProgressStateQuery } from "../state/checklist-progress-state.query";
import { ChecklistProgressStateService } from "../state/checklist-progress-state.service";
import { FileUploadsModalService } from "source/app/modals/file-uploads/file-uploads-modal-service";
import { FormControl, FormGroup, FormRecord, Validators } from "@angular/forms";
import { BehaviorSubject, Observable, combineLatest, defer, map, of, startWith, switchMap } from "rxjs";
import {
	AssignmentWithData,
	Checklist,
	FormAnswerGroup,
	FormQuestion,
	ImageAttachment,
	PageElement,
	Question,
	RequirementTrigger,
	Response,
} from "../checklist.interface";
import { ChecklistService } from "source/app/features/checklists/services/checklist.service";
import { LogicProperties, LogicService } from "../services/logic.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { RideOpsUiService } from "source/app/configuration/services/rideops-ui.service";
import { LocationStateQuery } from "source/app/configuration/state/location-state.query";

@Component({
	selector: "app-checklist-modal",
	templateUrl: "./checklist-modal.component.html",
	styleUrls: ["./checklist-modal.component.scss"],
})
@UntilDestroy()
export class ChecklistModalComponent implements OnInit {
	public static returnCodes = {
		submitted: "Submitted",
		noContent: "NoContent",
	};

	constructor(
		private checklistProgressQuery: ChecklistProgressStateQuery,
		private checklistProgressService: ChecklistProgressStateService,
		private loadingService: LoadingService,
		private modalController: ModalController,
		private alertController: AlertController,
		private translocoService: TranslocoService,
		private FileUploadsModalService: FileUploadsModalService,
		private checklistService: ChecklistService,
		private logicService: LogicService,
		private rideOpsUi: RideOpsUiService,
		private locationStateQuery: LocationStateQuery,
	) {}

	activePage: ChecklistPageWithVisibility;
	visiblePages$: Observable<ChecklistPageWithVisibility[]>;
	overallProgress$: Observable<ChecklistProgress>;
	pageMeta$: Observable<{ current: number; total: number }>;
	hasUnanswered$: Observable<boolean>;
	activePageId$ = new BehaviorSubject("");
	isFirstPage = true;
	isLastPage = true;
	locationName$ = this.locationStateQuery.locationName$;
	checklistTitle = "";

	@ViewChild(IonContent) private ionContent?: IonContent;

	form: FormRecord<FormGroup<FormAnswerGroup>>;
	ready = false;
	tocCollapsed = false;
	private submitting = false;

	isV2 = (): boolean => this.rideOpsUi.isV2Active();

	progressPercent = (progress: ChecklistProgress): number => {
		if (progress.total <= 0) {
			return 0;
		}
		return Math.round((progress.completed / progress.total) * 100);
	};

	cancel = async () => {
		await this.modalController.dismiss();
	};

	goToNextUnanswered = async () => {
		const pages = await this.visiblePages$.firstAsync();

		for (const page of pages) {
			for (const pageElement of page.elements) {
				if (pageElement.element.stereotype !== "Question") {
					continue;
				}

				const control = this.form.get(pageElement.element.id);
				if (control == null || control.disabled || control.valid) {
					continue;
				}

				this.activePageId$.next(page.id);
				setTimeout(() => {
					document
						.getElementById(`checklist-question-${pageElement.element.id}`)
						?.scrollIntoView({ behavior: "smooth", block: "center" });
				}, 50);
				return;
			}
		}
	};

	submit = async () => {
		if (this.submitting) {
			return;
		}

		this.submitting = true;
		//we need to know if there are any images, and if they are uploaded
		const imagesThatCantBeTranslated = await this.checklistService.imagesThatCantbeTranslated(this.form);

		if (imagesThatCantBeTranslated.length > 0) {
			const modal = await this.FileUploadsModalService.create(imagesThatCantBeTranslated);

			await modal.present();
			await modal.onDidDismiss();
			const newImagesThatCantBeTranslated = await this.checklistService.imagesThatCantbeTranslated(this.form);
			if (newImagesThatCantBeTranslated.length > 0) {
				//images stll in queu, user can click submit again later
				this.submitting = false;
				return;
			}
		}

		const loading = await this.loadingService.createLoader();
		loading.present();
		try {
			const checklistSubmitDelayMessage = setTimeout(() => {
				loading.message = this.translocoService.translate("CHECKLIST_SUBMIT_SUBMIT__TIME_DELAY");
			}, 7500);

			const checklist = await this.checklistProgressQuery.checklist$.firstAsync();
			const visiblePages = await this.visiblePages$.firstAsync();

			await this.checklistService.submit(checklist, this.form, visiblePages);
			clearTimeout(checklistSubmitDelayMessage);

			await this.modalController.dismiss(ChecklistModalComponent.returnCodes.submitted);
			loading.dismiss();
		} catch (_error) {
			loading.dismiss();

			const alert = await this.alertController.create({
				header: this.translocoService.translate("CHECKLIST_SUBMIT_ERROR__HEADER"),
				subHeader: this.translocoService.translate("CHECKLIST_SUBMIT_ERROR__SUB_HEADER"),
				message: this.translocoService.translate("CHECKLIST_SUBMIT_ERROR__MESSAGE"),
				buttons: [this.translocoService.translate("CHECKLIST_SUBMIT_ERROR__OK")],
				cssClass: this.rideOpsUi.isV2Active() ? "operators-v2-dialog" : undefined,
			});
			await alert.present();

			this.submitting = false;
		}
	};

	nextPage = async () => {
		const visiblePages = await this.visiblePages$.firstAsync();
		const currentIndex = visiblePages.findIndex((page) => page.id === this.activePage.id);

		if (currentIndex < visiblePages.length - 1) {
			const page = visiblePages[currentIndex + 1];
			this.activePageId$.next(page.id);
			this.scrollAfterPageChange(page);
		}
	};

	previousPage = async () => {
		const visiblePages = await this.visiblePages$.firstAsync();
		const currentIndex = visiblePages.findIndex((page) => page.id === this.activePage.id);

		if (currentIndex > 0) {
			const page = visiblePages[currentIndex - 1];
			this.activePageId$.next(page.id);
			this.scrollAfterPageChange(page);
		}
	};

	goToPage = async (pageId: string) => {
		const visiblePages = await this.visiblePages$.firstAsync();
		const page = visiblePages.find((visiblePage) => visiblePage.id === pageId);

		if (page != null) {
			this.activePageId$.next(pageId);
			this.scrollAfterPageChange(page);
		}
	};

	private scrollAfterPageChange = (page: ChecklistPageWithVisibility) => {
		setTimeout(async () => {
			for (const pageElement of page.elements) {
				if (pageElement.element.stereotype !== "Question") {
					continue;
				}

				const control = this.form.get(pageElement.element.id);
				if (control == null || control.disabled || control.valid) {
					continue;
				}

				document
					.getElementById(`checklist-question-${pageElement.element.id}`)
					?.scrollIntoView({ behavior: "smooth", block: "start" });
				return;
			}

			if (this.isV2()) {
				document.querySelector(".checklist-v2-content")?.scrollTo({ top: 0, behavior: "smooth" });
				return;
			}

			await this.ionContent?.scrollToTop(300);
		}, 50);
	};

	private createForm = async (checklist: Checklist): Promise<FormRecord<FormGroup<FormAnswerGroup>>> => {
		const checklistForm = new FormRecord<FormGroup<FormAnswerGroup>>({});

		checklist.pages.forEach((page) => {
			page.elements.forEach((element) => {
				if (element.stereotype !== "Question") {
					return;
				}

				const formGroup = this.createFormGroup();
				if (page.logic != null || element.logic != null) {
					//If Logic is defined, the question is not available, until logic triggers the question
					formGroup.disable();
				}

				checklistForm.addControl(element.id, formGroup);
			});
		});

		return checklistForm;
	};

	private createFormGroup = (): FormGroup<FormAnswerGroup> => {
		return new FormGroup<FormAnswerGroup>({
			question: new FormControl<FormQuestion>({ disabled: false, value: undefined }, [Validators.required]),
			comment: new FormControl<string>({ value: "", disabled: false }),
			attachments: new FormControl<ImageAttachment[]>({ value: [], disabled: false }),
			assignments: new FormControl<AssignmentWithData[]>({ value: [], disabled: false }),
		});
	};

	private getFormValue = (resultValues: Response[], checklist: Checklist): Record<string, FormResponse> => {
		const elements = checklist.pages.flatMap((page) => page.elements);
		const elementIds = elements.map((element) => element.id);

		const record: Record<string, FormResponse> = {};

		resultValues.forEach((response) => {
			// we filter out elements, that are no longer included in the checklist
			if (elementIds.includes(response.question) == false) {
				return;
			}

			const {
				answers,
				answered,
				position,
				user,
				question: key,
				comment,
				imageAttachments: attachments,
				assignments: assignments = [],
			} = response;

			const question: FormQuestion = {
				answers,
				answered,
				position,
				user,
				adHocScheduleSlots: [],
			};

			record[key] = {
				question,
				comment,
				attachments,
				assignments: assignments.map((x) => {
					return {
						assignment: x,
						solution: x.solution,
						triggeredBy: this.checklistService.getAssignmentTrigger(elements, x),
					};
				}),
			};
		});

		return record;
	};

	private initializeChecklistLogic = (checklist: Checklist, logicProperties: LogicProperties) => {
		const elementsVisible: { id: string; isVisible$: Observable<boolean> }[] = [];
		const pages: ChecklistPageWithVisibility[] = checklist.pages.map((page) => {
			const isPageVisible$: Observable<boolean> = this.logicService.evaluatePageLogic(
				page,
				logicProperties,
				elementsVisible,
				this.form,
			);

			const elements: CheklistPageElementWithVisibility[] = page.elements.map((element) => {
				const isElementVisible$: Observable<boolean> = this.logicService.evaluateElementLogic(
					isPageVisible$,
					element,
					logicProperties,
					elementsVisible,
					this.form,
				);

				elementsVisible.push({ id: element.id, isVisible$: isElementVisible$ });

				//This is to keep the observeables hot until we exit the checklist
				isElementVisible$.pipe(untilDestroyed(this)).subscribe();

				if (element.stereotype === "Question") {
					const questionElement = element as Question;
					const requirementTriggers: RequirementTrigger[] = questionElement.triggers.filter(
						(trigger) => trigger.type === "RequirementTrigger",
					) as RequirementTrigger[];

					if (requirementTriggers.length > 0) {
						const requirementObserveable$ = this.logicService.evaluateRequirementTriggerLogic(
							element.id,
							requirementTriggers,
							this.form,
						);

						requirementObserveable$.pipe(untilDestroyed(this)).subscribe();
					}
				}

				return {
					isVisible$: combineLatest([isPageVisible$, isElementVisible$]).pipe(
						map(([isPageVisible, isElementVisible]) => isPageVisible && isElementVisible),
					),
					element: element,
				};
			});

			const questionElementIds = page.elements
				.filter((element) => element.stereotype == "Question")
				.map((element) => element.id);

			const questionStatuses$ =
				questionElementIds.length === 0
					? of<string[]>([])
					: combineLatest(
							questionElementIds.map((elementId) =>
								defer(() =>
									this.form
										.get(elementId)
										.statusChanges.pipe(startWith(this.form.get(elementId).status)),
								),
							),
						);

			const progress$: Observable<ChecklistProgress> = questionStatuses$.pipe(
				map((statuses) => {
					const countable = statuses.filter((status) => status !== "DISABLED");
					return {
						completed: countable.filter((status) => status === "VALID").length,
						total: countable.length,
					};
				}),
			);

			const isCompleted$ = progress$.pipe(
				map((progress) => progress.total === 0 || progress.completed === progress.total),
			);

			return {
				...page,
				name: page.translatedName,
				isCompleted$,
				progress$,
				isVisible$: isPageVisible$,
				elements,
			};
		});

		this.visiblePages$ =
			pages.length === 0
				? of([])
				: combineLatest(pages.map((page) => page.isVisible$.pipe(map((visible) => ({ page, visible }))))).pipe(
						map((results) => results.filter((result) => result.visible).map((result) => result.page)),
					);

		this.overallProgress$ = this.visiblePages$.pipe(
			switchMap((visiblePages) => {
				if (visiblePages.length === 0) {
					return of({ completed: 0, total: 0 });
				}

				return combineLatest(visiblePages.map((page) => page.progress$)).pipe(
					map((pageProgresses) =>
						pageProgresses.reduce(
							(sum, progress) => ({
								completed: sum.completed + progress.completed,
								total: sum.total + progress.total,
							}),
							{ completed: 0, total: 0 },
						),
					),
				);
			}),
		);

		this.hasUnanswered$ = this.overallProgress$.pipe(
			map((progress) => progress.total > 0 && progress.completed < progress.total),
		);

		this.pageMeta$ = combineLatest([this.activePageId$, this.visiblePages$]).pipe(
			map(([activePageId, visiblePages]) => {
				const currentId = activePageId || visiblePages[0]?.id;
				const index = visiblePages.findIndex((page) => page.id === currentId);
				return {
					current: index >= 0 ? index + 1 : 1,
					total: visiblePages.length,
				};
			}),
		);
	};

	private saveFormOnChanges = () => {
		this.form.valueChanges.pipe(untilDestroyed(this)).subscribe((values) => {
			const responses: Response[] = Object.entries(values)
				.filter(([key, _value]) => this.form.controls[key].valid && this.form.controls[key].enabled)
				.map(([key, value]) => ({
					answers: value.question.answers,
					answered: value.question.answered,
					position: value.question.position,
					user: value.question.user,
					question: key,
					comment: value.comment,
					imageAttachments: value.attachments,
					adHocScheduleSlots: value.question.adHocScheduleSlots,
					assignments: value.assignments.map((x) => {
						return {
							...x.assignment,
							solution: x.solution,
						};
					}),
				}));

			this.checklistProgressService.setResponses(responses);
		});
	};

	async ngOnInit(): Promise<void> {
		const checklist = await this.checklistProgressQuery.checklist$.firstAsync();
		const resultValues = await this.checklistProgressQuery.responses$.firstAsync();
		const logicProperties = await this.logicService.getLogicProperties(checklist);

		this.form = await this.createForm(checklist);
		const formValue = this.getFormValue(resultValues, checklist);
		this.form.patchValue(formValue);

		// We dont currently support language switching, so we use the checklist default language
		this.checklistService.translateChecklist(checklist, checklist.language);
		this.checklistService.parseDocumentation(checklist);
		this.checklistTitle = checklist.name?.[checklist.language] ?? Object.values(checklist.name ?? {})[0] ?? "";
		this.initializeChecklistLogic(checklist, logicProperties);
		this.saveFormOnChanges();

		const visiblePages = await this.visiblePages$.firstAsync();
		if (visiblePages.length === 0) {
			await this.modalController.dismiss(ChecklistModalComponent.returnCodes.noContent);
			return;
		}

		combineLatest([this.activePageId$, this.visiblePages$])
			.pipe(untilDestroyed(this))
			.subscribe(([activePageId, pages]) => {
				if (pages.length === 0) {
					return;
				}

				const currentPageId = activePageId || pages[0].id;
				this.activePage = pages.find((page) => page.id === currentPageId);
				this.isFirstPage = pages[0].id === currentPageId;
				this.isLastPage = pages[pages.length - 1].id === currentPageId;
			});

		this.ready = true;
	}
}

export type FormResponse = Partial<{
	question: FormQuestion;
	comment: string;
	attachments: ImageAttachment[];
	assignments: AssignmentWithData[];
}>;

export interface ChecklistProgress {
	completed: number;
	total: number;
}

export interface ChecklistPageWithVisibility {
	id: string;
	name: string;
	isVisible$: Observable<boolean>;
	isCompleted$: Observable<boolean>;
	progress$: Observable<ChecklistProgress>;
	elements: CheklistPageElementWithVisibility[];
}

export interface CheklistPageElementWithVisibility {
	isVisible$: Observable<boolean>;
	element: PageElement;
}
